import type { Pagination, SortOrder } from '@/shared/lib';

export enum DocumentSendFormat {
  ELECTRONIC = 'ELECTRONIC',
  PHYSICAL = 'PHYSICAL',
  ELECTRONIC_PHYSICAL = 'ELECTRONIC_PHYSICAL',
}

export enum DocumentSendStatus {
  EMAIL_DISPATCH_IN_PROGRESS = 'EMAIL_DISPATCH_IN_PROGRESS',
  SENT = 'SENT',
  PARTIALLY_SENT = 'PARTIALLY_SENT',
}

export enum DocumentSendAudienceType {
  CLIENT = 'CLIENT',
  SUPPLIER = 'SUPPLIER',
  CLIENT_SUPPLIER = 'CLIENT_SUPPLIER',
}

export enum DocumentSendDocumentType {
  MANUAL = 'MQ',
  PROCEDURE = 'PR',
  WORK_INSTRUCTION = 'IT',
  DATA = 'DA',
}

export interface DocumentSendListItem {
  id: number;
  title: string;
  sendDate: string;
  sender: {
    id: number;
    name: string;
  };
  recipients: string[];
  format: DocumentSendFormat;
  status: DocumentSendStatus;
}

export interface DocumentSendProtheusParty {
  code: string;
  store: string;
}

export interface DocumentSendFilters {
  documentTypes?: DocumentSendDocumentType[];
  formats?: DocumentSendFormat[];
  hasElectronicCopy?: boolean;
  protheusClient?: DocumentSendProtheusParty;
  protheusSupplier?: DocumentSendProtheusParty;
  sendDate?: string;
}

export type DocumentSendSortField = 'title' | 'sendDate';

export interface DocumentSendListParams extends DocumentSendFilters {
  page: number;
  itemsPerPage: number;
  term?: string;
  sort?: DocumentSendSortField;
  order?: SortOrder;
}

export interface DocumentSendExportParams extends DocumentSendFilters {
  term?: string;
  sort?: DocumentSendSortField;
  order?: SortOrder;
}

export interface DocumentSendApiResponse {
  success: boolean;
  data: DocumentSendListItem[];
  metadata: Pagination;
}

export type DocumentSendRecipientType = 'CLIENT' | 'SUPPLIER';
export type DocumentSendAttachmentSourceType = 'UPLOAD' | 'URL';

export interface DocumentSendDetail {
  header: {
    code: string;
    typeDescription: string | null;
  };
  title: string;
  format: DocumentSendFormat;
  status: DocumentSendStatus;
  message: string | null;
  sender: {
    name: string;
    position: string;
    sendDate: string;
  };
  electronicRecipients: DocumentSendElectronicRecipient[];
  physicalRecipients: DocumentSendPhysicalRecipient[];
  attachments: DocumentSendAttachment[];
  documents: DocumentSendDetailDocument[];
}

export interface DocumentSendDetailDocument {
  id: number;
  prefix: string;
  title: string | null;
  revision: number;
  electronicRecipients: DocumentSendElectronicRecipient[];
  physicalRecipients: DocumentSendPhysicalRecipient[];
  attachments: DocumentSendAttachment[];
  message: string | null;
}

export interface DocumentSendElectronicRecipient {
  recipientType: DocumentSendRecipientType;
  name: string;
  email: string | null;
}

export interface DocumentSendPhysicalRecipient {
  recipientType: DocumentSendRecipientType;
  name: string;
  copies: number;
}

export interface DocumentSendAttachment {
  sourceType: DocumentSendAttachmentSourceType;
  fileName: string;
  externalUrl: string | null;
}

export interface DocumentSendDetailApiResponse {
  success: boolean;
  data: DocumentSendDetail;
}

export interface DocumentSendReportDocument {
  documentRevisionId: number;
  prefix: string;
  title: string | null;
  revision: number;
}

export interface DocumentSendReportItem {
  id: number;
  title: string;
  format: DocumentSendFormat;
  status: DocumentSendStatus;
  message: string | null;
  electronicRecipients: DocumentSendElectronicRecipient[];
  physicalRecipients: DocumentSendPhysicalRecipient[];
  attachments: DocumentSendAttachment[];
  sender: {
    name: string;
    position: string;
    sendDate: string;
  };
}

export interface DocumentSendReport {
  document: DocumentSendReportDocument;
  sends: DocumentSendReportItem[];
}

export interface DocumentSendReportApiResponse {
  success: boolean;
  data: DocumentSendReport;
}

export interface DocumentSendRecipientReportRecipient {
  recipientType: DocumentSendRecipientType;
  protheusCode: string;
  protheusStore: string;
  name: string;
  email: string | null;
}

export interface DocumentSendRecipientReportDocument {
  prefix: string;
  title: string | null;
  revision: number;
  copies: number | null;
}

export interface DocumentSendRecipientReportItem {
  id: number;
  title: string;
  format: DocumentSendFormat;
  status: DocumentSendStatus;
  documents: DocumentSendRecipientReportDocument[];
  attachments: DocumentSendAttachment[];
  sender: {
    name: string;
    position: string;
    sendDate: string;
  };
}

export interface DocumentSendReportHeader {
  code: string;
  revision: string;
}

export interface DocumentSendRecipientReport {
  header: DocumentSendReportHeader;
  recipient: DocumentSendRecipientReportRecipient;
  sends: DocumentSendRecipientReportItem[];
}

export interface DocumentSendRecipientReportParams {
  recipientType: DocumentSendRecipientType;
  protheusCode: string;
  protheusStore: string;
}

export interface DocumentSendRecipientReportApiResponse {
  success: boolean;
  data: DocumentSendRecipientReport;
}

export interface CreateDocumentSendPhysicalRecipientPayload {
  recipientType: DocumentSendRecipientType;
  protheusCode: string;
  protheusStore: string;
  recipientNameSnapshot: string;
  copies: number;
  sendDate: string;
}

export interface CreateDocumentSendDocumentPayload {
  documentRevisionId: number;
  physicalRecipients: CreateDocumentSendPhysicalRecipientPayload[];
}

export interface CreateDocumentSendPayload {
  title: string;
  sendDate: string;
  senderPositionId: number;
  documents: CreateDocumentSendDocumentPayload[];
}

export interface CreateCombinedDocumentSendRecipientPayload {
  type: DocumentSendRecipientType;
  protheusCode: string;
  protheusStore: string;
  name: string;
  email: string;
}

export interface CreateCombinedDocumentSendPhysicalRecipientPayload {
  recipientType: DocumentSendRecipientType;
  protheusCode: string;
  protheusStore: string;
  recipientNameSnapshot: string;
  copies: number;
  sendDate: string;
}

export interface CreateCombinedDocumentSendDocumentPayload {
  documentRevisionId: number;
  physicalRecipients: CreateCombinedDocumentSendPhysicalRecipientPayload[];
}

export interface CreateCombinedDocumentSendLocalUrlPayload {
  name: string;
  url: string;
}

export interface CreateCombinedDocumentSendMetadataPayload {
  idempotencyKey: string;
  title: string;
  message: string;
  sendDate: string;
  senderMemberId: number;
  senderPositionId: number;
  recipients: CreateCombinedDocumentSendRecipientPayload[];
  documents: CreateCombinedDocumentSendDocumentPayload[];
  localUrls: CreateCombinedDocumentSendLocalUrlPayload[];
}

export interface CombinedDocumentSendFailedRecipient {
  name: string;
  email: string;
}

export interface CreateCombinedDocumentSendResponse {
  id: number;
  status: DocumentSendStatus;
  failedEmailRecipients: CombinedDocumentSendFailedRecipient[];
}
